import { blobToObjectURL, createCsv, download } from '@project/utils/file';
import { action, autorun, makeObservable, observable } from 'mobx';
import { Feature } from 'ol';
import { utils, writeFile } from 'xlsx';

export type CartogramsTableData = Record<string | number, string | number>;
export type CartogramsTableColumnData = {
  dataIndex: string | number;
  title: string;
};

const CARTOGRAM_TABLE_COLUMN = 'cartogram';

export class CartogramTableStore {
  @observable cartogram?: string = undefined;
  @observable features?: Feature[] = undefined;
  @observable computeColumn?: string = undefined;

  @observable tableData?: CartogramsTableData[];
  @observable tableColumnsData?: CartogramsTableColumnData[];

  dispose?(): void;

  constructor() {
    makeObservable(this);
  }

  bind = () => {
    this.dispose = autorun(() => {
      const tableData = this.createTableData();
      const tableColumnsData = this.getCartogramTableColumnsSettings({
        columnKey: 'name',
      });

      this.setTableData(tableData);
      this.setTableColumnsData(tableColumnsData);
    });
  };

  unbind = () => this.dispose?.();

  private createTableData = ({
    featureKey,
    cartogramKey = CARTOGRAM_TABLE_COLUMN,
  }: {
    featureKey?: string;
    cartogramKey?: string;
  } = {}) => {
    if (!this.computeColumn || !this.cartogram) return undefined;

    const cartogramRow = this.features?.reduce<CartogramsTableData>(
      (acc, curr) => {
        const featureId = curr.getId();
        const featureColumn = featureKey ? curr.get(featureKey) : undefined;
        const featureValue = curr.get(this.computeColumn!) ?? 0;

        if ((featureColumn && featureColumn !== featureKey) || featureId)
          acc[featureColumn ?? featureId] = featureValue;

        return acc;
      },
      {},
    );

    const tableInfo = { [cartogramKey]: this.cartogram };

    if (!cartogramRow) return [tableInfo];

    return [{ ...tableInfo, ...cartogramRow }];
  };

  private getCartogramTableColumnsSettings = ({
    columnKey,
  }: { columnKey?: string } = {}) => {
    const featuresColumns = this.features
      ?.map((feature) => {
        const featureId = feature.getId();
        const featureValue = columnKey ? feature.get(columnKey) : undefined;

        if (!featureId) return;

        return { dataIndex: featureId, title: featureValue ?? featureId };
      })
      .filter((column) => column !== undefined);

    const cartogramColumn = {
      dataIndex: CARTOGRAM_TABLE_COLUMN,
      title: 'Таблица',
    };

    if (!featuresColumns) return [cartogramColumn];

    return [cartogramColumn, ...featuresColumns] as CartogramsTableColumnData[]; // Без присваивания вызывает ошибку в tslint
  };

  private getTableFileName = () => {
    if (!this.cartogram) return;

    return this.cartogram.split(' ').join('_');
  };

  exportAsCsv = () => {
    const data = this.createTableData({
      featureKey: 'name',
      cartogramKey: 'Таблица',
    });
    if (!data) return;

    const blobTableCsv = createCsv(data);
    const tableCsv = blobToObjectURL(blobTableCsv);
    const fileName = this.getTableFileName();

    if (fileName) {
      return download(tableCsv, `${fileName}.csv`);
    }
  };

  exportAsXlsx = () => {
    const data = this.createTableData({
      featureKey: 'name',
      cartogramKey: 'Таблица',
    });
    if (!data) return;

    const worksheet = utils.json_to_sheet(data);

    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    const fileName = this.getTableFileName();

    if (fileName) {
      return writeFile(workbook, `${fileName}.xlsx`);
    }
  };

  @action setCartogram = (cartogram?: string) => {
    this.cartogram = cartogram;
  };

  @action setFeatures = (features?: Feature[]) => {
    this.features = features;
  };

  @action setComputeColumn = (computeColumn?: string) => {
    this.computeColumn = computeColumn;
  };

  @action setTableData = (tableData?: CartogramsTableData[]) => {
    this.tableData = tableData;
  };

  @action setTableColumnsData = (
    tableColumnData?: CartogramsTableColumnData[],
  ) => {
    this.tableColumnsData = tableColumnData;
  };

  @action reset = () => {
    this.setCartogram(undefined);
    this.setFeatures(undefined);
    this.setComputeColumn(undefined);
    this.setTableData(undefined);
    this.setTableColumnsData(undefined);
  };
}
